import _ from 'lodash';

/*
 * Api calls
 */

// Returns captcha headers
function getCaptchaHeaders(captcha) {
  return {
    ...(captcha?.v3token && { 'X-RECAPTCHA-V3-TOKEN': captcha.v3token }),
    ...(captcha?.v2token && { 'X-RECAPTCHA-V2-TOKEN': captcha.v2token }),
  };
}

function getOtpHeaders(otp) {
  return {
    ...(otp && { 'X-OTP-TOKEN': otp }),
  };
}

// Returns captcha and login headers with otp
function getLoginHeaders(captcha, otp) {
  return {
    ...getCaptchaHeaders(captcha),
    ...getOtpHeaders(otp),
  };
}

// Returns headers only when called from within SSR server init block. For frontend API calls are headers included automatically
function getAuthHeaders() {
  return import.meta.server ? useRequestHeaders(['cookie']) : {};
}

// Fetch helper that adds base path to all api calls, supports SSR by using NUXT_API_PROXY_URL for invoking direct backend call
// Should used for all calls
function fetchWrapper(path, options = {}) {
  const config = useRuntimeConfig();
  const base = import.meta.server ? config.apiProxyUrl : '/api';
  return $fetch(`${base}${path}`, options);
}

// Fetch wrapper that includes credentials and server auth headers
export async function fetchWithAuth(request, options = {}) {
  const baseOptions = {
    credentials: 'include',
    headers: getAuthHeaders(),
    ...options,
  };

  try {
    return await fetchWrapper(request, { ...baseOptions });
  } catch (error) {
    if (error.response?.status === 401) {
      const route = useRoute();
      await navigateToWithRedirect('login', route);
      throw error;
    }
    throw error;
  }
}

/*
 * Auth (public)
 */

export async function register(body, captcha) {
  return fetchWrapper('/auth/register', { method: 'POST', body, headers: getCaptchaHeaders(captcha) });
}

export async function login(body, captcha, otp) {
  return fetchWrapper('/auth/login', { method: 'POST', body, headers: getLoginHeaders(captcha, otp) });
}

export async function socialLogin(body, captcha, otp) {
  return fetchWrapper('/auth/social-login', { method: 'POST', body, headers: getLoginHeaders(captcha, otp) });
}

export async function logout() {
  return fetchWrapper('/auth/logout', { method: 'POST' });
}

export async function activate(body) {
  return fetchWrapper('/auth/activate', { method: 'POST', body });
}

export async function resend(body) {
  return fetchWrapper('/auth/resend', { method: 'POST', body });
}

export async function forgot(body) {
  return fetchWrapper('/auth/reset', { method: 'POST', body });
}

export async function resetPassword(body, otp) {
  return fetchWrapper('/auth/reset-password', { method: 'POST', body, headers: getOtpHeaders(otp) });
}

export async function validateResetToken(body) {
  return fetchWrapper('/auth/validate-reset-token', { method: 'POST', body });
}

export async function getSymbols(filter) {
  return fetchWrapper('/symbols', { method: 'GET', query: filter });
}

// TODO unused for now
// export async function refreshToken(headers) {
//   return fetchWrapper('/auth/refresh', { method: 'POST', headers, credentials: 'include' });
// }

/*
 * Payment (public)
 */

export async function getPayment(paymentId) {
  return fetchWrapper(`/payments/${paymentId}`, { method: 'GET' });
}

export async function getPaymentOptions(paymentId) {
  return fetchWrapper(`/payments/${paymentId}/options`, { method: 'GET' });
}

export async function setPaymentOption(paymentId, body) {
  return fetchWrapper(`/payments/${paymentId}/set-option`, { method: 'POST', body });
}

/*
 * User session (authorized)
 */

/*
 * Profile
 */

export async function getMyProfile() {
  return fetchWithAuth('/users/me');
}

export async function updateMyProfile(body) {
  return fetchWithAuth('/users/me', { method: 'PUT', body });
}

/*
 * OTP
 */

export async function generateOTP() {
  return fetchWithAuth('/otp/generate', { method: 'POST' });
}

export async function verifyOTP(body) {
  return fetchWithAuth('/otp/verify', { method: 'POST', body });
}

export async function resetOTP(body) {
  return fetchWithAuth('/otp/reset', { method: 'POST', body });
}

/*
 * Keys
 */

export async function getKeys() {
  return fetchWithAuth('/keys', { method: 'GET' });
}

export async function createKey(body, otp) {
  return fetchWithAuth('/keys', { method: 'POST', body, headers: getOtpHeaders(otp) });
}

export async function updateKey(id, body, otp) {
  return fetchWithAuth(`/keys/${id}`, { method: 'PUT', body, headers: getOtpHeaders(otp) });
}

export async function deleteKey(id, otp) {
  return fetchWithAuth(`/keys/${id}`, { method: 'DELETE', headers: getOtpHeaders(otp) });
}

/*
 * Payments
 */

export async function createPayment(body) {
  return fetchWithAuth('/payments', { method: 'POST', body });
}

export async function getPayments(filter) {
  return fetchWithAuth('/payments', { method: 'GET', params: _.omitBy(filter, _.isNil) });
}

/*
 * Settlements
 */

export async function configureSettlement(body) {
  return fetchWithAuth('/settlements/configure', { method: 'POST', body });
}

export async function createSettlementWallet(body) {
  return fetchWithAuth('/settlements/wallets', { method: 'POST', body });
}

export async function getSettlementWallets() {
  return fetchWithAuth('/settlements/wallets', { method: 'GET' });
}

export async function updateSettlementWallet(id, body) {
  return fetchWithAuth(`/settlements/wallets/${id}`, { method: 'PUT', body });
}

export async function deleteSettlementWallet(id) {
  return fetchWithAuth(`/settlements/wallets/${id}`, { method: 'DELETE' });
}

export async function getSettlementBalances() {
  return fetchWithAuth('/settlements/balances', { method: 'GET' });
}

export async function submitSettlementRequest(body, otp) {
  return fetchWithAuth('/settlements/submit', { method: 'POST', body, headers: getOtpHeaders(otp) });
}

export async function getSettlements(filter) {
  return fetchWithAuth('/settlements', { method: 'GET', params: _.omitBy(filter, _.isNil) });
}

export async function getSettlementKeys(network, address, code) {
  return fetchWithAuth(`/settlements/keys/${network}/${address}`, { method: 'GET', headers: getOtpHeaders(code) });
}
